/**
 * Patrimonio Page — Card-based UI for Activos
 * src/pages/Patrimonio.jsx
 */

import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { logActivity } from '../utils/auditLog';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);
const fmtShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1_000_000) return `$${(n/1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(n/1_000).toFixed(0)}K`;
  return fmt(n);
};

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

const COLORS = ['#8b5cf6', '#3b82f6'];

const SEED_ACTIVOS = [
  { person: 'Tumi',    tipo: 'Dólares',             monto: 18700      },
  { person: 'Tumi',    tipo: 'Caja de Ahorro /SAN', monto: 2940000    },
  { person: 'Tumi',    tipo: 'AFORE MEX',            monto: 2679518    },
  { person: 'Tumi',    tipo: 'Unit Linked',          monto: 12499603   },
  { person: 'Tumi',    tipo: 'Plan De Retiro GNP',   monto: 300000     },
  { person: 'Tumi',    tipo: 'CETES',                monto: 400000     },
  { person: 'Tumi',    tipo: 'Depa Roma',            monto: 1447055    },
  { person: 'Tumi',    tipo: 'Casa Bolivia',         monto: 700000     },
  { person: 'Alberto', tipo: 'Ambiente Coworking',   monto: 7000000    },
  { person: 'Alberto', tipo: 'Casa Coatzacoalcos',   monto: 7000000    },
];

const SEED_PASIVOS = [
  { person: 'Tumi',    tipo: 'Préstamo Nómina 1',   monto: 400000,  pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Préstamos Nómina 2',  monto: 900000,  pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Amex',                 monto: 20000,   pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Like U',               monto: 6000,    pagoMensual: 0, meses: 0 },
  { person: 'Tumi',    tipo: 'Platino Mastercard',   monto: 6000,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Tumi',                 monto: 900000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'BBVA Créditos (2)',    monto: 200000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'MercadoPago',          monto: 115000,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex Saldo',           monto: 218861,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex Diferidos',       monto: 316927,  pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Amex MSI',             monto: 62582,   pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Like U',               monto: 9770,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'BBVA Visa',            monto: 8700,    pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Hey',                  monto: 380,     pagoMensual: 0, meses: 0 },
  { person: 'Alberto', tipo: 'Nu',                   monto: 520,     pagoMensual: 0, meses: 0 },
];

async function seedCollection(db, colName, seedData) {
  const snap = await getDocs(collection(db, colName));
  if (snap.empty) {
    for (const item of seedData) await addDoc(collection(db, colName), item);
    const newSnap = await getDocs(collection(db, colName));
    return newSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.82rem' }}>
      <strong>{payload[0].name}</strong><br />{fmt(payload[0].value)}
    </div>
  );
};

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = { id: null, person: 'Tumi', tipo: '', monto: 0, saving: false };

export default function Patrimonio({ db, user }) {
  const [activos,  setActivos]  = useState([]);
  const [pasivos,  setPasivos]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [act, pas] = await Promise.all([
          seedCollection(db, 'activos', SEED_ACTIVOS),
          seedCollection(db, 'pasivos', SEED_PASIVOS),
        ]);
        setActivos(act);
        setPasivos(pas);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const totalActivos   = activos.reduce((s, a) => s + (parseFloat(a.monto) || 0), 0);
  const totalPasivos   = pasivos.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const patrimonioNeto = totalActivos - totalPasivos;

  const tumiActivos    = activos.filter(a => a.person === 'Tumi').reduce((s, a) => s + (a.monto || 0), 0);
  const albertoActivos = activos.filter(a => a.person === 'Alberto').reduce((s, a) => s + (a.monto || 0), 0);

  const chartData = [
    { name: 'Tumi',    value: tumiActivos    },
    { name: 'Alberto', value: albertoActivos },
  ];

  const openAdd  = () => setModal({ ...BLANK_MODAL });
  const openEdit = (row) => setModal({ id: row.id, person: row.person, tipo: row.tipo || '', monto: row.monto || 0, saving: false });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person: modal.person,
        tipo:   modal.tipo,
        monto:  parseFloat(modal.monto) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'activos', modal.id), data);
        setActivos(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Activo', { tipo: data.tipo, monto: data.monto, person: data.person });
        showToast('Activo actualizado');
      } else {
        const ref = await addDoc(collection(db, 'activos'), data);
        setActivos(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Activo', { tipo: data.tipo, monto: data.monto, person: data.person });
        showToast('Activo agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'activos', modal.id));
      setActivos(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Activo', { tipo: modal.tipo, monto: modal.monto, person: modal.person });
      setModal(null);
      showToast('Activo eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Patrimonio — Activos <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1a472a', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> Agregar
        </button>
      </div>

      {/* Summary cards */}
      <div className="summary-cards" style={{ marginBottom: '1rem' }}>
        <div className="summary-card">
          <div className="summary-card-label">Total Activos</div>
          <div className="summary-card-value" style={{ color: '#10b981' }}>{fmt(totalActivos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label" style={{ color: '#8b5cf6' }}>Tumi</div>
          <div className="summary-card-value" style={{ color: '#8b5cf6' }}>{fmt(tumiActivos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label" style={{ color: '#3b82f6' }}>Alberto</div>
          <div className="summary-card-value" style={{ color: '#3b82f6' }}>{fmt(albertoActivos)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Patrimonio Neto</div>
          <div className="summary-card-value" style={{ color: patrimonioNeto >= 0 ? '#10b981' : '#ef4444' }}>{fmt(patrimonioNeto)}</div>
        </div>
      </div>

      {/* Pie chart */}
      <div className="card" style={{ padding: '1rem 0.5rem 0.5rem', marginBottom: '1rem' }}>
        <p style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 0.25rem' }}>Activos por Persona</p>
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" innerRadius={38} outerRadius={56} paddingAngle={3} dataKey="value">
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', paddingBottom: '0.5rem' }}>
          {chartData.map((d, i) => (
            <span key={i}><span style={{ color: COLORS[i], fontWeight: 700 }}>●</span> {d.name} {fmtShort(d.value)}</span>
          ))}
        </div>
      </div>

      {/* Pasivos link */}
      <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem', color: 'var(--text-secondary)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '0.625rem', padding: '0.5rem 0.875rem' }}>
        Total Pasivos: <strong style={{ color: '#ef4444' }}>{fmt(totalPasivos)}</strong> · Gestiona tus pasivos en la página <strong>Pasivos</strong>.
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {activos.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin activos. Agrega uno con el botón +.
          </div>
        )}

        {activos.map(row => (
          <div key={row.id} onClick={() => openEdit(row)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
            {/* Person color bar */}
            <div style={{ width: 4, height: 44, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />
            {/* Center info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person), marginBottom: '0.15rem' }}>{row.person}</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.tipo}</div>
            </div>
            {/* Right: monto */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#10b981' }}>{fmt(row.monto)}</div>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? 'Editar' : 'Agregar'} Activo
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>Persona</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo */}
              <div>
                <label style={labelStyle}>Tipo</label>
                <input type="text" value={modal.tipo} onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))} placeholder="Ej. CETES, Inmueble, Dólares..." style={inputStyle} />
              </div>

              {/* Monto */}
              <div>
                <label style={labelStyle}>Monto (MXN)</label>
                <input type="number" inputMode="decimal" value={modal.monto} onChange={e => setModal(m => ({ ...m, monto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} /> Eliminar
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
